<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="test-package-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="Quality Control"
              :list="[
                { value: 1, text: 'Yes - Quality Control' },
                { value: 0, text: 'No - Quality Control' },
              ]"
              :selected="show_quality_control"
              @onSelect="toggleColumn('quality_control', $event.value)"
            />
          </b-col>

          <b-col class="pr-0">
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event); fetchAnalytes()"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="test_package_samples.analytes.id"
              filter-operator="eq"
              :disabled="!customFilters.sample_type"
              @onSelect="setCustomFilter('analyte', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="name" slot-scope="{ row }">
        <a :href="`/#/testpackages/${row.id}`" class="btn-link">
          {{ row.name }}
        </a>
      </span>
      <span slot="sample_type.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['sample_type']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/sampletypes/${getData(row, {
                levels: ['sample_type'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["sample_type"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="test_package_group.name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="['test_package_group']"
          :field="'name'"
        >
          <template #existing-content>
            <router-link
              :to="`/testpackagegroups/${getData(row, {
                levels: ['test_package_group'],
                field: 'id',
              })}`"
            >
              {{
                getData(row, { levels: ["test_package_group"], field: "name" })
              }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="cost" slot-scope="{ row }">
        {{ formatCost(row.cost) }}
      </span>
      <span slot="cost_add_on" slot-scope="{ row }">
        <template v-if="row.cost_add_on">
          {{ formatCost(row.cost_add_on) }}
        </template>
      </span>
      <span slot="quality_control" slot-scope="{ row }">
        {{ row.quality_control ? 'Yes' : 'No' }}
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit test package')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`/#/testpackages/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete test package')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import SelectFilter from "@/components/shared/SelectFilter";
import TestPackageCollection from "@/collections/lab/TestPackageCollection";
import AnalyteModel from "@/models/lab/AnalyteModel";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "TestPackageServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ServerTable,
    SelectFilter,
    ModalTrashBasket,
  },
  props: {
    collection: {
      type: Object,
      default: () => new TestPackageCollection(),
    },
    analytes: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    turnaround_sample_type_id: {
      type: String,
      default: null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "name",
        "sample_type.name",
        "test_package_group.name",
        "description",
        "cost",
        "cost_add_on",
        "quality_control",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "name",
        "sample_type.name",
        "test_package_group.name",
        "cost",
      ],
    },
    show_delete_modal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit test package") ||
      this.checkPermission("delete test package");

    return {
      entity_type: "testPackage",
      laboratory_id: this.$route.params.laboratory_id || null,
      sample_id: this.$route.params.sample_id || null,
      submission_id: this.$route.params.submission_id || null,
      turnaround_id: this.$route.params.turnaround_id || null,
      analyte_collection: null,
      sample_type_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            "sample_type.name": "Type",
            "test_package_group.name": "Group",
            cost_add_on: "Add-On Cost",
            quality_control: "QC",
          },
        },
      }),
      customFilters: [
        {
          "field": "quality_control",
          "operator": "eq",
          "value": "0",
          "logic": "AND"
        }
      ],
      show_quality_control: 0
    };
  },
  computed: {
    ...mapGetters({
      trash: "testPackageTrash",
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection,
      };
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete test package") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    formatCost(value) {
      let formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      });
      return formatter.format(value);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    prepareCollectionFilter(filter) {
      this.sample_id &&
        filter.filterEquals("test_package_samples.id", this.sample_id);
      this.laboratory_id &&
        filter.filterEquals("laboratory_id", this.laboratory_id);
      this.submission_id &&
        filter.filterEquals(
          "test_package_samples.sample_test_packages.sample.submission_id",
          this.submission_id
        );
      this.turnaround_sample_type_id &&
        filter.filterEquals("sample_type_id", this.turnaround_sample_type_id);

      return filter;
    },
    async init() {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
          sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
          
        }));
    },
    async fetchAnalytes() {
      this.setCustomFilter('analyte', null)
      if (this.analytes) return;
      this.analyte_collection = null

      let filters = []

      if (this.customFilters.sample_type) {
        filters.push({
          field: "sample_type_id",
          logic: "filterEquals",
          value: this.customFilters.sample_type.value
        })
      }

      this.analyte_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "analyte",
      });
    },
  },
  created() {
    this.init();

    this.toggleColumn("quality_control", this.show_quality_control);
  },
};
</script>
